import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Container, CircularProgress, Typography, Button, Snackbar, Alert as MuiAlert } from "@mui/material";
import { theme } from "../theme/colors";
import { useUser } from "../context/UserContext";
import { getPostDetail, upvotePost, removeUpvote, followPost, unfollowPost, deletePost, reportPost } from "../utils/feedApi";
import { blockUser } from "../utils/discoverApi";
import PostCard from "../components/CommunityComponents/PostCard";
import VerifyIdentityModal from "../components/VerifyIdentityModal";
import AuthRequiredModal from "../components/AlertComponents/AuthRequiredModal";
import ReportModal from "../components/AlertComponents/ReportModal";

export default function PostDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, userId, verifiedUser } = useUser() || {};
  const isLoggedIn = !!user;
  const isVerified = typeof verifiedUser === "boolean" ? verifiedUser : false;

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [postToReport, setPostToReport] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });

  const handleActionCountChange = useCallback((postToUpdate, { logged, actionCount, verifiedActionCount, delta }) => {
    setPost((prev) => {
      if (!prev || prev.id !== postToUpdate.id) return prev;
      return {
        ...prev,
        action_count:
          typeof actionCount === "number"
            ? actionCount
            : Math.max(0, (prev.action_count || 0) + delta),
        verified_action_count:
          typeof verifiedActionCount === "number"
            ? verifiedActionCount
            : isVerified
              ? Math.max(0, (prev.verified_action_count ?? 0) + delta)
              : prev.verified_action_count,
        viewer_has_logged_action: logged,
      };
    });
  }, [isVerified]);

  const loadPost = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPostDetail(postId);
      setPost({
        ...data,
        has_upvoted: data.has_upvoted ?? data.viewer_has_upvoted,
        has_followed: data.has_followed ?? data.viewer_is_following,
        follower_count: data.follower_count ?? data.follow_count ?? 0,
      });
    } catch (err) {
      console.error("Failed to load post", err);
      setError("This post isn't available. It may have been removed or you may not have access.");
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    if (postId) {
      loadPost();
    }
  }, [postId, loadPost]);

  const handleUpvote = async (postToUpdate) => {
    if (!isLoggedIn) { setShowAuthModal(true); return; }

    const currentlyUpvoted = postToUpdate.has_upvoted ?? postToUpdate.viewer_has_upvoted;
    try {
      setPost(prev => ({
        ...prev,
        has_upvoted: !currentlyUpvoted,
        upvote_count: currentlyUpvoted ? Math.max(0, (prev.upvote_count || 0) - 1) : (prev.upvote_count || 0) + 1
      }));

      if (currentlyUpvoted) {
        await removeUpvote(postToUpdate.id);
      } else {
        await upvotePost(postToUpdate.id);
      }
    } catch (err) {
      console.error("Failed to toggle upvote", err);
      loadPost();
    }
  };

  const handleFollow = async (postToUpdate) => {
    if (!isLoggedIn) { setShowAuthModal(true); return; }

    const currentlyFollowed = postToUpdate.has_followed ?? postToUpdate.viewer_is_following;
    try {
      setPost(prev => ({
        ...prev,
        has_followed: !currentlyFollowed,
        follower_count: currentlyFollowed ? Math.max(0, (prev.follower_count || prev.follow_count || 0) - 1) : (prev.follower_count || prev.follow_count || 0) + 1
      }));

      if (currentlyFollowed) {
        await unfollowPost(postToUpdate.id);
      } else {
        await followPost(postToUpdate.id);
      }
    } catch (err) {
      console.error("Failed to toggle follow", err);
      loadPost();
    }
  };

  const handleDelete = async (postToDelete) => {
    try {
      await deletePost(postToDelete.id);
      navigate("/community");
    } catch (err) {
      console.error("Failed to delete post", err);
    }
  };

  const handleReport = (postToReportObj) => {
    setPostToReport(postToReportObj);
    setShowReportModal(true);
  };

  const handleBlock = async ({ userId: authorId }) => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (!authorId) return;
    try {
      await blockUser(authorId);
      navigate("/community");
    } catch (err) {
      console.error("Failed to block user", err);
      showToast(err?.message || "Couldn't block this account. Please try again.", "error");
      throw err;
    }
  };

  const handleReportSubmit = async (reason, details) => {
    if (!postToReport) return;
    if (!isLoggedIn) {
      setShowReportModal(false);
      setPostToReport(null);
      setShowAuthModal(true);
      return;
    }
    try {
      await reportPost(postToReport.id, reason, details);
      setShowReportModal(false);
      setPostToReport(null);
      showToast("Post reported successfully.");
    } catch (err) {
      console.error("Failed to report post", err);
      const msg = err?.response?.data?.error || "";
      showToast(msg || "Failed to report post. Please try again.", "error");
      throw err;
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc" }}>
        <CircularProgress sx={{ color: theme.primary }} />
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#f8fafc", p: 4 }}>
        <Typography variant="h6" sx={{ color: "#64748b", mb: 2 }}>{error || "Post not found."}</Typography>
        <Button variant="contained" onClick={() => navigate("/community")} sx={{ background: theme.gradient, color: '#fff', boxShadow: 'none', '&:hover': { background: theme.gradient, opacity: 0.92, color: '#fff' } }}>Back to Community</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 4 }}>
      <Container maxWidth="md" sx={{ px: { xs: 2, md: 0 } }}>
        <PostCard 
          post={post} 
          currentUserId={userId}
          onUpvote={handleUpvote}
          onFollow={handleFollow}
          onAuthRequired={() => setShowAuthModal(true)}
          isVerified={isVerified}
          onVerifyRequired={() => setShowVerifyModal(true)}
          onEdit={() => navigate("/community/create", { state: { editPostId: post.id } })}
          onDelete={handleDelete}
          onReport={handleReport}
          onBlock={handleBlock}
          onActionCountChange={handleActionCountChange}
          onActionError={(message) => showToast(message, "error")}
          defaultCommentsExpanded
          isDetailView
        />
      </Container>

      <VerifyIdentityModal 
        isOpen={showVerifyModal} 
        onClose={() => setShowVerifyModal(false)} 
      />

      <AuthRequiredModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={() => {
          setShowAuthModal(false);
          navigate("/auth/signup");
        }}
        title="You're browsing the community"
        description="Create a free account to vote, comment, and join the discussion."
        confirmLabel="Sign up"
        dismissLabel="Keep browsing"
      />

      <ReportModal
        open={showReportModal}
        contentType="post"
        onClose={() => {
          setShowReportModal(false);
          setPostToReport(null);
        }}
        onSubmit={handleReportSubmit}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}